/**
 * SK ID allocation.
 *
 * SK IDs are sequential and human-readable, which means — unlike the UUIDs
 * everything else uses — they cannot be minted independently on two devices
 * without colliding. That is why only the front desk PC creates members.
 *
 * The counter lives in local settings and is re-seeded from the highest ID
 * actually present after every sync pull, so a reinstalled or restored device
 * resumes from the right number instead of re-issuing SK-0001.
 */
import { db, getSettingNumber, setSetting, SETTING_KEYS } from './db';
import { formatSkId, highestSkSequence, parseSkId } from '@shared/identity';

/**
 * Re-point the counter at the highest sequence in the members table.
 *
 * Only ever moves forward. If a pull brings in SK-0100 from another device we
 * jump ahead; if the local counter is already higher we keep it, because those
 * numbers may have been handed out on printed receipts already.
 */
export async function reseedSkIdCounter(): Promise<number> {
  const all = await db.members.toArray();
  // Soft-deleted members keep their ID reserved — reissuing the SK ID of a
  // deleted member would make two people share a number in the paper trail.
  const highestInDb = highestSkSequence(all.map((m) => m.sk_id));
  const counter = await getSettingNumber(SETTING_KEYS.skIdCounter, 0);
  const next = Math.max(counter, highestInDb);
  if (next !== counter) await setSetting(SETTING_KEYS.skIdCounter, String(next));
  return next;
}

/**
 * Take the next SK ID.
 *
 * MUST be called inside a Dexie transaction that has both `settings` and
 * `members` in scope — otherwise two quick saves could read the same counter
 * and mint the same ID. `createSubscription` does exactly that.
 *
 * Defends against a counter that has fallen behind the data (restored backup,
 * interrupted write) by scanning forward until the ID is genuinely free.
 */
export async function allocateSkId(): Promise<string> {
  const current = await getSettingNumber(SETTING_KEYS.skIdCounter, 0);

  let candidate = current + 1;
  // Bounded so a corrupt counter can never spin forever; 10k probes is far
  // beyond any real gap while still terminating fast.
  for (let probe = 0; probe < 10_000; probe++) {
    const skId = formatSkId(candidate);
    const clash = await db.members.where('sk_id').equals(skId).first();
    if (!clash) {
      await setSetting(SETTING_KEYS.skIdCounter, String(candidate));
      return skId;
    }
    // Someone already holds this number — jump past them and try again.
    candidate = Math.max(candidate + 1, (parseSkId(clash.sk_id) ?? candidate) + 1);
  }

  throw new Error(
    'Could not allocate an SK ID: the counter is far behind the members table. Check Settings → Sync log.'
  );
}
